import { useCurrency } from '../contexts/CurrencyContext';
import { PLANS } from '../config/pricing';
import React, { useState } from 'react';
import { useUser } from '../contexts/UserContext';

const UpgradePanel = () => {
  const { user } = useUser();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { formatPrice } = useCurrency();
  const currentPlan = user.plan || 'free';

  const allPlans = {
    pro: {
      name: 'Creator Pro',
      icon: String.fromCodePoint(0x1F680),
      color: '#8b5cf6',
      tagline: 'For serious creators who want viral content daily',
      badge: 'MOST POPULAR',
      price: PLANS.pro.price,
      limits: { clips: '100', videos: '20' },
      features: [
        { text: '20 videos per month', included: true },
        { text: '5 clips per video', included: true },
        { text: '1080p HD portrait output (1080x1920)', included: true },
        { text: 'No watermarks', included: true },
        { text: 'AI hotspot detection', included: true },
        { text: 'Batch processing', included: true },
        { text: 'Priority support', included: true },
        { text: 'Share to social platforms', included: true },
        { text: 'Advanced AI models', included: true },
        { text: 'Custom clip duration', included: false },
        { text: 'Commercial rights', included: false },
        { text: 'API access', included: false }
      ]
    },
    business: {
      name: 'Business',
      icon: String.fromCodePoint(0x1F3E2),
      color: '#10b981',
      tagline: 'For agencies and businesses managing multiple creators',
      badge: 'BEST VALUE',
      price: PLANS.business.price,
      limits: { clips: '480', videos: '60' },
      features: [
        { text: '60 videos per month', included: true },
        { text: '8 clips per video', included: true },
        { text: '1080p HD portrait output (1080x1920)', included: true },
        { text: 'No watermarks', included: true },
        { text: 'AI hotspot detection', included: true },
        { text: 'Batch processing', included: true },
        { text: 'Priority support', included: true },
        { text: 'Share to social platforms', included: true },
        { text: 'Advanced AI models', included: true },
        { text: 'Custom clip duration', included: true },
        { text: 'Commercial rights', included: true },
        { text: 'API access (1000 req/hour)', included: true }
      ]
    }
  };

  const handleUpgrade = async (planType) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          planType: planType,
          billingPeriod: billingCycle === 'monthly' ? 'monthly' : 'yearly'
        })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Checkout is not available yet. Stripe integration coming soon!');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Checkout is not available yet. Stripe integration coming soon!');
    }
  };

  const getPrice = (plan) => {
    return billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly;
  };

  const getHeading = () => {
    if (currentPlan === 'free' || currentPlan === 'starter') return 'Upgrade Your Plan';
    if (currentPlan === 'pro') return 'Compare Plans';
    if (currentPlan === 'business') return 'Manage Your Plan';
    return 'Your Plans';
  };

  const getSubheading = () => {
    if (currentPlan === 'free' || currentPlan === 'starter') return 'Unlock more videos, clips, and premium features to grow your audience faster';
    if (currentPlan === 'pro') return 'See how Business compares to your current Creator Pro plan';
    if (currentPlan === 'business') return 'Compare your current plan or downgrade to Creator Pro';
    return '';
  };

  const getCurrentPlanLabel = () => {
    if (currentPlan === 'free' || currentPlan === 'starter') return 'Free Tier - 5 videos/month, 15 clips/month, watermarked, 720p quality';
    if (currentPlan === 'pro') return 'Creator Pro - 50 videos/month, 150 clips/month, 1080p, no watermarks';
    if (currentPlan === 'business') return 'Business - 60 videos/month, 180 clips/month, commercial rights, API access';
    return currentPlan;
  };

  const renderPlanCard = (key, plan) => {
    const isCurrentPlan = currentPlan === key;
    const isUpgrade = (currentPlan === 'free' || currentPlan === 'starter') || (currentPlan === 'pro' && key === 'business');
    const isDowngrade = (currentPlan === 'business' && key === 'pro');

    return React.createElement('div', {
      key: key,
      className: 'upgrade-card',
      style: {
        background: 'linear-gradient(135deg, rgba(30,30,50,0.9) 0%, rgba(20,20,40,0.95) 100%)',
        border: isCurrentPlan ? '2px solid ' + plan.color : '2px solid ' + plan.color + '80',
        borderRadius: '20px',
        padding: '32px',
        position: 'relative',
        overflow: 'hidden',
        opacity: isCurrentPlan && currentPlan === 'pro' ? 0.85 : 1
      }
    },
      // Badge
      React.createElement('div', {
        style: {
          position: 'absolute', top: '0', left: '0', right: '0',
          background: isCurrentPlan ? 'linear-gradient(135deg, #6b7280, #4b5563)' : key === 'pro' ? 'linear-gradient(135deg, #f97316, #ef4444)' : 'linear-gradient(135deg, #10b981, #059669)',
          padding: '8px', textAlign: 'center', color: '#fff', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase'
        }
      }, isCurrentPlan ? 'YOUR CURRENT PLAN' : plan.badge),

      // Plan Header
      React.createElement('div', { style: { textAlign: 'center', marginTop: '24px', marginBottom: '24px' } },
        React.createElement('div', { style: { fontSize: '40px', marginBottom: '8px' } }, plan.icon),
        React.createElement('h2', { style: { fontSize: '28px', fontWeight: '700', color: '#fff', marginBottom: '4px' } }, plan.name),
        React.createElement('p', { style: { color: '#94a3b8', fontSize: '14px', marginBottom: '20px' } }, plan.tagline),
        React.createElement('div', { style: { marginBottom: '8px' } },
          React.createElement('span', { style: { fontSize: '48px', fontWeight: '800', color: '#fff' } }, formatPrice(getPrice(plan))),
          React.createElement('span', { style: { color: '#94a3b8', fontSize: '16px' } }, '/mo')
        ),
        billingCycle === 'yearly' ?
          React.createElement('div', null,
            React.createElement('div', { style: { display: 'inline-block', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', marginBottom: '6px' } },
              'Save ' + formatPrice(plan.price.yearlySaving) + '/year'),
            React.createElement('div', { style: { color: '#94a3b8', fontSize: '13px', marginTop: '4px' } },
              formatPrice(plan.price.yearlyTotal) + '/year total - Billed annually')
          ) :
          React.createElement('div', { style: { color: '#94a3b8', fontSize: '13px' } }, 'Billed monthly')
      ),

      // Limits Box
      React.createElement('div', {
        style: { display: 'flex', justifyContent: 'center', gap: '32px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', padding: '16px', marginBottom: '24px' }
      },
        React.createElement('div', { style: { textAlign: 'center' } },
          React.createElement('div', { style: { fontSize: '28px', fontWeight: '800', color: plan.color } }, plan.limits.clips),
          React.createElement('div', { style: { fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' } }, 'Clips/Month')
        ),
        React.createElement('div', { style: { textAlign: 'center' } },
          React.createElement('div', { style: { fontSize: '28px', fontWeight: '800', color: plan.color } }, plan.limits.videos),
          React.createElement('div', { style: { fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' } }, 'Videos/Month')
        )
      ),

      // Features
      React.createElement('div', { style: { marginBottom: '24px' } },
        plan.features.map((feature, idx) =>
          React.createElement('div', {
            key: idx,
            style: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: idx < plan.features.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }
          },
            React.createElement('span', { style: { fontSize: '16px', width: '22px', textAlign: 'center', flexShrink: 0 } }, feature.included ? String.fromCodePoint(0x2705) : String.fromCodePoint(0x274C)),
            React.createElement('span', { style: { color: feature.included ? '#e2e8f0' : '#4b5563', fontSize: '14px', fontWeight: feature.included ? '500' : '400', textDecoration: feature.included ? 'none' : 'line-through' } }, feature.text)
          )
        )
      ),

      // Action Button
      isCurrentPlan ?
        React.createElement('div', {
          style: { width: '100%', padding: '16px', borderRadius: '12px', border: '2px solid ' + plan.color + '40', background: plan.color + '10', color: plan.color, fontSize: '16px', fontWeight: '700', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }
        }, String.fromCodePoint(0x2705) + ' Current Plan') :
      isUpgrade ?
        React.createElement('button', {
          onClick: () => handleUpgrade(key),
          style: { width: '100%', padding: '16px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, ' + plan.color + ', ' + plan.color + 'cc)', color: '#ffffff', fontSize: '16px', fontWeight: '700', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer' }
        }, 'Upgrade to ' + plan.name) :
        React.createElement('div', { style: { width: '100%', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', fontSize: '14px', fontWeight: '600', textAlign: 'center' } }, 'Current or lower tier')
  );
  };

  return React.createElement('div', { style: { maxWidth: '1000px', margin: '0 auto', padding: '20px' } },
      React.createElement('div', { style: { padding: '12px 0 8px 0' } }, React.createElement('button', { className: 'dash-back-btn', onClick: function() { window.history.pushState({section:'dashboard'}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate', {state:{section:'dashboard'}})); } }, '🏠 ← Dashboard')),
    React.createElement('style', null, '.upgrade-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;margin-bottom:40px}@media(max-width:768px){.upgrade-grid{grid-template-columns:1fr!important;gap:16px}.upgrade-card{padding:24px 16px!important}}'),

    // Header
    React.createElement('div', { style: { textAlign: 'center', marginBottom: '40px' } },
      React.createElement('h1', { style: { fontSize: '42px', fontWeight: '900', color: '#ffffff', textShadow: '0 2px 10px rgba(139,92,246,0.5)', marginBottom: '12px' } }, getHeading()),
      React.createElement('p', { style: { color: '#ffffff', fontSize: '18px', fontWeight: '700', marginBottom: '24px' } }, getSubheading()),
      // Billing Toggle
      React.createElement('div', { style: { display: 'inline-flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.05)', padding: '6px 8px', borderRadius: '50px', border: '1px solid rgba(255,255,255,0.1)' } },
        React.createElement('button', { onClick: () => setBillingCycle('monthly'), style: { padding: '8px 20px', borderRadius: '50px', border: 'none', background: billingCycle === 'monthly' ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'transparent', color: billingCycle === 'monthly' ? '#fff' : '#94a3b8', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.3s ease' } }, 'Monthly'),
        React.createElement('button', { onClick: () => setBillingCycle('yearly'), style: { padding: '8px 20px', borderRadius: '50px', border: 'none', background: billingCycle === 'yearly' ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent', color: billingCycle === 'yearly' ? '#fff' : '#94a3b8', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.3s ease' } }, 'Yearly'),
        billingCycle === 'yearly' ? React.createElement('span', { style: { background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' } }, 'Save 21%') : null
      )
    ),

    // Plan Cards
    React.createElement('div', { className: 'upgrade-grid' },
      renderPlanCard('pro', allPlans.pro),
      renderPlanCard('business', allPlans.business)
    ),

    // Current Plan Footer
    React.createElement('div', { style: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '20px', textAlign: 'center' } },
      React.createElement('p', { style: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: 0 } }, String.fromCodePoint(0x1F4CB) + ' Your current plan: ' + getCurrentPlanLabel())
    ),

    // Footer
    React.createElement('div', { style: { marginTop: '40px', textAlign: 'center' } },
      React.createElement('p', { style: { color: '#ffffff', fontSize: '14px', fontWeight: '700' } }, String.fromCodePoint(0x1F4B3) + ' Secure payment powered by Stripe ' + String.fromCharCode(8226) + ' Cancel anytime '),
      React.createElement('p', { style: { color: '#ffffff', fontSize: '14px', fontWeight: '700', marginTop: '8px' } }, 'Questions? Contact us at ',
        React.createElement('a', { href: 'mailto:vlogclipai@outlook.com', style: { color: '#8b5cf6' } }, 'vlogclipai@outlook.com')
      )
    )
  );
};

export default UpgradePanel;
